import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FaComments, FaPaperPlane } from 'react-icons/fa';
import { ADMIN_FAQS, FAQ_CATEGORIES, getFaqsByCategory } from './adminFaqs';
import './AdminFaqChatbot.css';

const normalizeText = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const tokenize = (value) => normalizeText(value).split(' ').filter(Boolean);

const findBestFaq = (text) => {
  const clean = normalizeText(text);
  if (!clean) return null;

  let best = null;
  let bestScore = 0;
  const queryTokens = tokenize(clean);

  ADMIN_FAQS.forEach((faq) => {
    const questionTokens = tokenize(faq.question);
    const keywordTokens = (faq.keywords || []).flatMap((k) => tokenize(k));
    const allTokens = new Set([...questionTokens, ...keywordTokens]);

    let score = 0;
    allTokens.forEach((token) => {
      if (queryTokens.includes(token)) score += 1;
    });

    if (clean.includes(normalizeText(faq.question))) score += 3;
    if (score > bestScore) {
      bestScore = score;
      best = faq;
    }
  });

  return bestScore >= 2 ? best : null;
};

const getFallback = () => {
  return {
    answer:
      'I could not find an exact FAQ for this. Please contact support for quick help.\nSupport: +91-9876543210\nEmail: support@assetflow.in',
    contactHint: 'You can also check View Plans or Manage Invoices from admin dashboard.'
  };
};

const AdminFaqChatbot = ({ isLoggedIn, userRole, userName }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedQuickQuestion, setSelectedQuickQuestion] = useState('');
  const [messages, setMessages] = useState(() => [
    {
      id: `bot-${Date.now()}`,
      from: 'bot',
      text: `Hi ${userName || 'Admin'}, I can help with plan purchase FAQs, OTP issues, invoices, and data export.`
    }
  ]);

  const shouldRender = useMemo(() => {
    if (!isLoggedIn || userRole !== 'admin') return false;
    const path = location.pathname || '';
    return path.startsWith('/lab-admin') || path === '/admin/buy-plan';
  }, [isLoggedIn, userRole, location.pathname]);

  if (!shouldRender) return null;

  const pushBotAnswer = (query) => {
    const match = findBestFaq(query);
    const response = match || getFallback();
    const text = response.contactHint
      ? `${response.answer}\n\n${response.contactHint}`
      : response.answer;

    setMessages((prev) => [
      ...prev,
      { id: `bot-${Date.now()}-${Math.random()}`, from: 'bot', text }
    ]);
  };

  const sendQuery = (queryText) => {
    const q = String(queryText || '').trim();
    if (!q) return;

    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}-${Math.random()}`, from: 'user', text: q }
    ]);

    setTimeout(() => {
      pushBotAnswer(q);
    }, 150);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = input;
    setInput('');
    sendQuery(q);
  };

  const handleCategorySelect = (value) => {
    setSelectedCategory(value);
    setSelectedQuickQuestion(''); // Reset question when category changes
  };

  const handleQuestionSelect = (value) => {
    setSelectedQuickQuestion(value);
    if (value) {
      setInput(value);
    }
  };

  // Get questions for the selected category
  const questionsInCategory = selectedCategory 
    ? getFaqsByCategory(selectedCategory).map(faq => faq.question)
    : [];

  return (
    <div className="admin-faq-chatbot">
      {isOpen ? (
        <div className="chatbot-panel" role="dialog" aria-label="Admin FAQ chatbot">
          <div className="chatbot-header">
            <div>
              <h4>Admin Help Chatbot</h4>
              <p>FAQ only, quick support guidance</p>
            </div>
            <button className="chatbot-close" onClick={() => setIsOpen(false)} aria-label="Close chatbot">
              ×
            </button>
          </div>

          <div className="chatbot-body">
            {messages.map((msg) => (
              <div key={msg.id} className={`chat-msg ${msg.from}`}>
                {msg.text}
              </div>
            ))}
          </div>

          <div className="quick-question-wrap">
            <p className="category-title"> Browse by Category</p>
            
            {/* Category tabs/buttons */}
            <div className="category-tabs">
              {FAQ_CATEGORIES.map((category) => (
                <button
                  key={category}
                  className={`category-tab ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => handleCategorySelect(category)}
                  title={category}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Question selector - only shows questions from selected category */}
            {selectedCategory && questionsInCategory.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <label style={{ fontSize: '12px', color: '#526a84', fontWeight: '600', display: 'block', marginBottom: '8px' }}>
                  💬 Questions in {selectedCategory}:
                </label>
                <select
                  value={selectedQuickQuestion}
                  onChange={(e) => handleQuestionSelect(e.target.value)}
                  aria-label="Select question in category"
                  className="questions-select"
                >
                  <option value="">Pick a question...</option>
                  {questionsInCategory.map((question) => (
                    <option key={question} value={question}>
                      {question}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <form className="chatbot-input" onSubmit={handleSubmit}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask plan/OTP/invoice/export question"
              maxLength={220}
            />
            <button type="submit" aria-label="Send question">
              <FaPaperPlane />
            </button>
          </form>
        </div>
      ) : (
        <button className="chatbot-fab" onClick={() => setIsOpen(true)} aria-label="Open chatbot">
          <FaComments />
        </button>
      )}
    </div>
  );
};

export default AdminFaqChatbot;
